// src/components/Landing/DemoPage.jsx
// Marketing demo page — uses the shared MarketingShell so the visual
// language matches PlaceholderLanding (and the rest of the marketing site).
// Replaces the prior bespoke chrome (broken /landing-dashboard.png mock,
// teal footer, duplicate logo+name nav). Real product screenshots come from
// /images/{guest_menu,kitchen_dashboard,admin_overview}.png.

import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { initializeAnalytics, trackPageView } from '../../utils/analytics';
import {
	Box,
	Flex,
	Text,
	Heading,
	Button,
	HStack,
	VStack,
	Container,
} from '@chakra-ui/react';
import { MarketingShell, Kicker, TOKENS } from './MarketingShell';
import { Reveal } from './motion';

const { BRAND, BRAND_DEEP, ACCENT, ACCENT_DEEP, INK, MUTED, CREAM, LINE, HEAD_FONT } = TOKENS;

// Same contract as the landing page: the WhatsApp number ships via build env,
// and when it's unset the WhatsApp button doesn't render at all (email stays).
const WHATSAPP_NUMBER = String(process.env.REACT_APP_WHATSAPP_NUMBER || '').replace(/[^0-9]/g, '');

// ── Reusable bits ─────────────────────────────────────────────────────

const PhoneFrame = ({ src, alt }) => (
	<Box
		position="relative"
		mx="auto"
		maxW={{ base: '280px', md: '320px' }}
		borderRadius="40px"
		bg={INK}
		p="10px"
		boxShadow="0 30px 60px -20px rgba(17,17,19,0.35), 0 8px 24px rgba(17,17,19,0.18)"
	>
		<Box
			borderRadius="32px"
			overflow="hidden"
			bg="white"
			aspectRatio="9 / 19"
			position="relative"
		>
			<Box
				as="img"
				src={src}
				alt={alt}
				w="100%"
				h="100%"
				objectFit="cover"
				objectPosition="top center"
				display="block"
			/>
			{/* Notch */}
			<Box
				position="absolute"
				top="10px"
				left="50%"
				transform="translateX(-50%)"
				w="110px"
				h="22px"
				bg={INK}
				borderRadius="full"
			/>
		</Box>
	</Box>
);

const TabletFrame = ({ src, alt }) => (
	<Box
		position="relative"
		mx="auto"
		maxW={{ base: '480px', md: '620px' }}
		borderRadius="20px"
		bg={INK}
		p="8px"
		boxShadow="0 30px 60px -20px rgba(17,17,19,0.3), 0 8px 24px rgba(17,17,19,0.14)"
	>
		<Box
			borderRadius="14px"
			overflow="hidden"
			bg="white"
			aspectRatio="16 / 11"
		>
			<Box as="img" src={src} alt={alt} w="100%" h="100%" objectFit="cover" objectPosition="top center" display="block" />
		</Box>
	</Box>
);

const Section = ({ children, bg = 'white', py = { base: 16, md: 24 }, ...rest }) => (
	<Box bg={bg} py={py} px={{ base: 6, md: 10, lg: 16 }} {...rest}>
		<Container maxW="1280px" px={0}>{children}</Container>
	</Box>
);

const SectionHeading = ({ kicker, title, lead, kickerColor }) => (
	<Reveal>
	<Box maxW="900px" mb={{ base: 10, md: 14 }}>
		<Kicker color={kickerColor}>{kicker}</Kicker>
		<Heading
			as="h2"
			mt={4}
			fontFamily={HEAD_FONT}
			fontWeight="700"
			fontSize={{ base: '32px', md: '52px' }}
			lineHeight="1.05"
			letterSpacing="-0.025em"
			color={INK}
		>
			{title}
		</Heading>
		{lead && (
			<Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color={MUTED} maxW="680px" lineHeight="1.55">
				{lead}
			</Text>
		)}
	</Box>
	</Reveal>
);

// ── Page ──────────────────────────────────────────────────────────────

const ROLE_VIEWS = [
	{
		role: 'Guests',
		image: '/images/guest_menu.webp',
		summary: 'Scan a QR at the table → menu opens in any browser → orders go straight to the right kitchen station.',
		bullets: [
			'No app to download, no account to create',
			'Dietary tags and allergies handled at the item level',
			'Live availability — when an item runs out it disappears from the menu',
			'Phones with the menu already open keep working through WiFi drops',
		],
	},
	{
		role: 'Kitchen team',
		image: '/images/kitchen_dashboard.webp',
		summary: 'A real-time queue per kitchen station. The grill team sees grill tickets; jollof station sees jollof tickets.',
		bullets: [
			'One tap to mark a dish Preparing, Ready, or Delivered',
			'Sound alert when a new ticket lands',
			'Per-item availability toggle — tap "out" and it drops off the guest menu',
			'Course-aware order grouping for multi-course service',
		],
	},
	{
		role: 'Servers',
		image: '/images/admin_overview.webp',
		summary: 'Servers see ready orders as they come up, route them to the right table, and mark delivered when the plate lands.',
		bullets: [
			'Live delivery queue across all kitchens',
			'Per-table order entry for walk-up or staff-assisted ordering',
			'Guest first name on every ticket — easier than calling table numbers',
			'Works on a tablet at the floor station, no laptop required',
		],
	},
];

const WORKFLOW_STEPS = [
	{
		step: '01',
		title: 'Create your event',
		desc: 'Name, date, venue, and team in five minutes. Or start with a sample event your account ships with — it has tables, menu items, and a kitchen station pre-loaded for you to explore.',
	},
	{
		step: '02',
		title: 'Build menu, stations, tables',
		desc: 'Add courses, dishes, dietary tags, and prices. Configure each kitchen station. Lay out tables by section (Hall A, VIP, etc.) and generate a unique QR per table.',
	},
	{
		step: '03',
		title: 'Invite your team',
		desc: 'Send invite links to your kitchen and floor staff. They sign up once and join the event with the right role automatically — no permissions table to manage.',
	},
	{
		step: '04',
		title: 'Go live',
		desc: 'Print QR codes, set tables, and open the event. Watch the live dashboard fill with orders and ticket transitions. Export a CSV summary at the end of the night.',
	},
];

const DemoPage = () => {
	useEffect(() => {
		initializeAnalytics();
		trackPageView('demo');
	}, []);

	return (
		<MarketingShell>
			{/* ── Hero ─────────────────────────────────────────────── */}
			<Section py={{ base: 12, md: 20 }}>
				<Box maxW="900px">
					<Kicker>Product tour</Kicker>
					<Heading
						as="h1"
						mt={5}
						fontFamily={HEAD_FONT}
						fontWeight="700"
						fontSize={{ base: '40px', md: '72px' }}
						lineHeight="1.02"
						letterSpacing="-0.03em"
						color={INK}
					>
						Three views of one event.{' '}
						<Box as="span" color={ACCENT}>Kept in sync.</Box>
					</Heading>
					<Text mt={6} fontSize={{ base: 'lg', md: 'xl' }} color={MUTED} maxW="700px" lineHeight="1.55">
						What the guest sees on their phone. What the kitchen sees on a tablet. What you see on your laptop as the planner. The same data, the same moment, no WhatsApp screenshots in between.
					</Text>
					<HStack mt={8} spacing={4} flexWrap="wrap">
						<Button
							as={RouterLink}
							to="/register"
							bg={ACCENT}
							color="white"
							borderRadius="full"
							px={8}
							h="56px"
							fontWeight="700"
							_hover={{ bg: ACCENT_DEEP, textDecoration: 'none' }}
							textDecoration="none"
						>
							Try a sample event →
						</Button>
						<Button
							as="button"
							onClick={() => {
								const el = document.getElementById('book-section');
								if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
							}}
							bg="transparent"
							color={INK}
							border={`1.5px solid ${INK}`}
							borderRadius="full"
							px={8}
							h="56px"
							fontWeight="700"
							_hover={{ bg: INK, color: 'white' }}
							textDecoration="none"
							cursor="pointer"
						>
							Book a walkthrough
						</Button>
					</HStack>
				</Box>
			</Section>

			{/* ── Hero composite (real screenshots) ─────────────────── */}
			<Section bg={CREAM} py={{ base: 12, md: 18 }}>
				<Box
					display="grid"
					gridTemplateColumns={{ base: '1fr', md: 'auto 1fr' }}
					gap={{ base: 10, md: 14 }}
					alignItems="center"
				>
					<PhoneFrame src="/images/guest_menu.webp" alt="Guest ordering on a phone" />
					<Box>
						<Kicker color={BRAND}>What the guest sees</Kicker>
						<Heading
							as="h3"
							mt={3}
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize={{ base: '28px', md: '40px' }}
							color={INK}
							lineHeight="1.1"
						>
							Scan, browse, order.
						</Heading>
						<Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color={MUTED} maxW="520px" lineHeight="1.55">
							Guests open a branded menu from the QR code at their seat. They pick courses with live availability and dietary tags, type their first name so the kitchen knows whose plate it is, then submit. The order lands in the right kitchen station.
						</Text>
					</Box>
				</Box>
			</Section>

			<Section py={{ base: 12, md: 18 }}>
				<Box
					display="grid"
					gridTemplateColumns={{ base: '1fr', md: '1fr auto' }}
					gap={{ base: 10, md: 14 }}
					alignItems="center"
				>
					<Box order={{ base: 2, md: 1 }}>
						<Kicker color={BRAND}>What the kitchen sees</Kicker>
						<Heading
							as="h3"
							mt={3}
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize={{ base: '28px', md: '40px' }}
							color={INK}
							lineHeight="1.1"
						>
							Every ticket, the moment it lands.
						</Heading>
						<Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color={MUTED} maxW="520px" lineHeight="1.55">
							A live queue at every station. One tap moves a ticket through Pending → Preparing → Ready → Delivered. Mark something sold out and it disappears from the guest menu instantly.
						</Text>
					</Box>
					<Box order={{ base: 1, md: 2 }}>
						<TabletFrame src="/images/kitchen_dashboard.webp" alt="Kitchen team seeing live orders on a tablet" />
					</Box>
				</Box>
			</Section>

			<Section bg={CREAM} py={{ base: 12, md: 18 }}>
				<Box
					display="grid"
					gridTemplateColumns={{ base: '1fr', md: 'auto 1fr' }}
					gap={{ base: 10, md: 14 }}
					alignItems="center"
				>
					<TabletFrame src="/images/admin_overview.webp" alt="Planner watching the event run live" />
					<Box>
						<Kicker color={BRAND}>What you see</Kicker>
						<Heading
							as="h3"
							mt={3}
							fontFamily={HEAD_FONT}
							fontWeight="700"
							fontSize={{ base: '28px', md: '40px' }}
							color={INK}
							lineHeight="1.1"
						>
							The whole event, on one screen.
						</Heading>
						<Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color={MUTED} maxW="520px" lineHeight="1.55">
							As the event planner, you watch the room come to life. Tables filling, kitchens cooking, plates landing. Stations get auto-routed by guest section. Live counts, completion %, and a CSV export at the end of service for reconciliation.
						</Text>
					</Box>
				</Box>
			</Section>

			{/* ── Role cards (the details) ──────────────────────────── */}
			<Section py={{ base: 16, md: 24 }}>
				<SectionHeading
					kicker="Three roles · three views"
					title="Built for the people in the room."
					lead="Each dashboard is built for one role. No more sharing a Google Sheet with the kitchen, the floor, and the planner — everyone sees what they need, nothing they don't."
				/>
				<Box
					display="grid"
					gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
					gap={{ base: 6, md: 8 }}
				>
					{ROLE_VIEWS.map((view) => (
						<VStack
							key={view.role}
							align="flex-start"
							spacing={5}
							bg="white"
							border={`1px solid ${LINE}`}
							borderRadius="20px"
							p={{ base: 6, md: 8 }}
							h="100%"
						>
							<Box
								px={3}
								py={1}
								bg="rgba(61, 30, 109, 0.08)"
								color={BRAND}
								borderRadius="full"
								fontSize="xs"
								fontWeight="700"
								textTransform="uppercase"
								letterSpacing="0.08em"
							>
								{view.role}
							</Box>
							<Text fontSize="md" color={INK} fontWeight="600" lineHeight="1.5">
								{view.summary}
							</Text>
							<VStack align="flex-start" spacing={2.5} mt="auto">
								{view.bullets.map((b) => (
									<HStack key={b} align="flex-start" spacing={3}>
										<Box mt="7px" w="6px" h="6px" borderRadius="full" bg={ACCENT} flexShrink={0} />
										<Text fontSize="sm" color={MUTED} lineHeight="1.5">{b}</Text>
									</HStack>
								))}
							</VStack>
						</VStack>
					))}
				</Box>
			</Section>

			{/* ── Workflow (numbered steps) ─────────────────────────── */}
			<Section bg={CREAM} py={{ base: 16, md: 24 }}>
				<SectionHeading
					kicker="Setup · 4 steps"
					title="From signup to live event."
					lead="Most events take 10 minutes to set up. Full-service bundles include day-before setup with our Ops Manager — you confirm the menu and we handle the rest."
					kickerColor={BRAND}
				/>
				<Box
					display="grid"
					gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
					gap={{ base: 6, md: 8 }}
				>
					{WORKFLOW_STEPS.map((s) => (
						<Box
							key={s.step}
							bg="white"
							border={`1px solid ${LINE}`}
							borderRadius="20px"
							p={{ base: 6, md: 7 }}
							h="100%"
						>
							<Text
								fontFamily={HEAD_FONT}
								fontWeight="800"
								fontSize="44px"
								color={ACCENT}
								lineHeight="1"
								mb={4}
							>
								{s.step}
							</Text>
							<Heading
								as="h4"
								fontFamily={HEAD_FONT}
								fontWeight="700"
								fontSize="xl"
								color={INK}
								mb={3}
							>
								{s.title}
							</Heading>
							<Text fontSize="sm" color={MUTED} lineHeight="1.6">
								{s.desc}
							</Text>
						</Box>
					))}
				</Box>
			</Section>

			{/* ── Book a walkthrough ──────────────────────────────── */}
			<Section bg={CREAM} py={{ base: 14, md: 18 }}>
				<Box id="book-section" maxW="900px" mx="auto" textAlign="center">
					<Kicker color={BRAND}>Book a walkthrough</Kicker>
					<Heading
						as="h2"
						mt={4}
						fontFamily={HEAD_FONT}
						fontWeight="700"
						fontSize={{ base: '28px', md: '40px' }}
						color={INK}
						lineHeight="1.1"
					>
						See it on your actual event.
					</Heading>
					<Text mt={4} fontSize={{ base: 'md', md: 'lg' }} color={MUTED} lineHeight="1.55">
						30-minute live session over Google Meet. Bring a real menu and a guess at your guest count — we'll run a sample event in front of you and you can poke at the kitchen and server dashboards.
					</Text>
					<HStack mt={8} spacing={4} flexWrap="wrap" justify="center">
						<Button
							as="a"
							href="mailto:jason.ikeokwu@gmail.com?subject=Eventory%20walkthrough%20request&body=Hi%20Jason%2C%0A%0AI%27d%20like%20a%20walkthrough%20of%20Eventory.%0A%0AEvent%20type%3A%20%0AExpected%20guest%20count%3A%20%0APreferred%20time%2Fday%3A%20%0A%0AThanks!"
							bg={ACCENT}
							color="white"
							borderRadius="full"
							px={8}
							h="56px"
							fontWeight="700"
							_hover={{ bg: ACCENT_DEEP, textDecoration: 'none' }}
							textDecoration="none"
						>
							Email us — jason.ikeokwu@gmail.com
						</Button>
						{WHATSAPP_NUMBER && (
							<Button
								as="a"
								href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi, I'd like a walkthrough of Eventory")}`}
								target="_blank"
								rel="noopener noreferrer"
								bg="white"
								color={INK}
								border={`1.5px solid ${INK}`}
								borderRadius="full"
								px={8}
								h="56px"
								fontWeight="700"
								_hover={{ bg: INK, color: 'white', textDecoration: 'none' }}
								textDecoration="none"
							>
								WhatsApp us
							</Button>
						)}
					</HStack>
					<Text mt={6} fontSize="sm" color={MUTED}>
						We get back to walkthrough requests within 24 hours, Monday–Friday.
					</Text>
				</Box>
			</Section>

			{/* ── Final CTA ────────────────────────────────────────── */}
			<Section bg={BRAND_DEEP} py={{ base: 16, md: 24 }}>
				<Box textAlign="center" maxW="800px" mx="auto" color="white">
					<Kicker color={ACCENT}>Ready to see it on a real event?</Kicker>
					<Heading
						as="h2"
						mt={5}
						fontFamily={HEAD_FONT}
						fontWeight="700"
						fontSize={{ base: '36px', md: '56px' }}
						lineHeight="1.05"
						color="white"
					>
						Your first two events of software are on us.
					</Heading>
					<Text mt={5} fontSize={{ base: 'md', md: 'lg' }} color="rgba(255,255,255,0.75)" lineHeight="1.55">
						No card required. Set up a sample event in five minutes, or invite us to walk through your next real event before you commit.
					</Text>
					<HStack mt={8} spacing={4} flexWrap="wrap" justify="center">
						<Button
							as={RouterLink}
							to="/register"
							bg={ACCENT}
							color="white"
							borderRadius="full"
							px={8}
							h="56px"
							fontWeight="700"
							_hover={{ bg: ACCENT_DEEP, textDecoration: 'none' }}
							textDecoration="none"
						>
							Create your free account →
						</Button>
						<Button
							as={RouterLink}
							to="/pricing"
							bg="transparent"
							color="white"
							border="1.5px solid rgba(255,255,255,0.4)"
							borderRadius="full"
							px={8}
							h="56px"
							fontWeight="700"
							_hover={{ bg: 'rgba(255,255,255,0.08)', textDecoration: 'none' }}
							textDecoration="none"
						>
							See pricing
						</Button>
					</HStack>
				</Box>
			</Section>
		</MarketingShell>
	);
};

export default DemoPage;
